import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { Bell, Check, MoreHorizontal, Settings, Trash2 } from 'lucide-react'
import { Menu, MenuItem, MenuSeparator } from '../ui'
import { useNotifications } from '../../contexts/NotificationContext'
import { useWatchlist } from '../../contexts/WatchlistContext'
import {
  deleteNotification,
  fetchNotifications,
  isUnread,
} from '../../services/notifications'
import {
  findWatchlistItem,
  type WatchlistEntityType,
  type WatchlistItem,
} from '../../services/watchlist'
import type { AppNotification, NotificationFacets } from '../../types/notifications'
import {
  notificationEntityLabel,
} from '../../utils/notificationLabels'
import { notificationHref } from '../../utils/notificationLinks'
import { notificationSections } from '../../utils/notificationPresentation'
import { useToast } from '../../contexts/ToastContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Container } from '../ui'
import NotificationItemContent from './NotificationItemContent'
import WatchlistItemNotificationModal from './WatchlistItemNotificationModal'
import InboxFilters, { type InboxFilterState } from './InboxFilters'

const PAGE_SIZE = 50

/** Inbox filters live in the URL (?ulaeste=1&typer=ownership,address&liste=3) so a view can be shared. */
function readFilters(params: URLSearchParams): InboxFilterState {
  const list = params.get('liste')
  return {
    unreadOnly: params.get('ulaeste') === '1',
    groups: (params.get('typer') ?? '').split(',').filter(Boolean),
    listId: list && /^\d+$/.test(list) ? Number(list) : null,
  }
}

function writeFilters(params: URLSearchParams, f: InboxFilterState): URLSearchParams {
  const next = new URLSearchParams(params)
  if (f.unreadOnly) next.set('ulaeste', '1')
  else next.delete('ulaeste')
  if (f.groups.length > 0) next.set('typer', f.groups.join(','))
  else next.delete('typer')
  if (f.listId) next.set('liste', String(f.listId))
  else next.delete('liste')
  return next
}

function filtersActive(f: InboxFilterState): boolean {
  return f.unreadOnly || f.groups.length > 0 || f.listId !== null
}

interface NotificationsInboxProps {
  /** When true, omits outer page chrome (used inside /account). */
  embedded?: boolean
}

function inboxSummary(total: number, unreadCount: number, filtered = false): string {
  if (filtered) {
    return `${total} ${total === 1 ? 'notifikation matcher' : 'notifikationer matcher'} · ${unreadCount} ulæste i alt`
  }
  if (total === 0) return 'Ingen notifikationer'
  if (unreadCount === 0) return `${total} i alt · alle læst`
  if (unreadCount === total) {
    return total === 1 ? '1 ulæst' : `${unreadCount} ulæste`
  }
  return `${unreadCount} ulæst${unreadCount === 1 ? '' : 'e'} · ${total} i alt`
}

const NotificationsInbox: React.FC<NotificationsInboxProps> = ({ embedded = false }) => {
  const { markRead, markAllRead, refresh: refreshBadge } = useNotifications()
  const { data: watchlistData, updateItemNotificationPreferences } = useWatchlist()
  const { showToast } = useToast()
  usePageTitle(embedded ? undefined : 'Notifikationer')

  const [items, setItems] = useState<AppNotification[]>([])
  const [total, setTotal] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [busy, setBusy] = useState(false)
  const [settingsItem, setSettingsItem] = useState<WatchlistItem | null>(null)
  const [facets, setFacets] = useState<NotificationFacets | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const filterKey = searchParams.toString()
  const filters = useMemo(() => readFilters(new URLSearchParams(filterKey)), [filterKey])

  const setFilters = (next: InboxFilterState) =>
    setSearchParams((prev) => writeFilters(prev, next), { replace: true })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchNotifications(PAGE_SIZE, 0, filters.unreadOnly, {
        listId: filters.listId,
        groups: filters.groups,
        facets: true,
      })
      setItems(data.items)
      setTotal(data.total)
      setUnreadCount(data.unreadCount)
      if (data.facets) setFacets(data.facets)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    } finally {
      setLoading(false)
    }
  }, [showToast, filters])

  useEffect(() => {
    void load()
  }, [load])

  async function handleLoadMore() {
    setLoadingMore(true)
    try {
      const data = await fetchNotifications(PAGE_SIZE, items.length, filters.unreadOnly, {
        listId: filters.listId,
        groups: filters.groups,
      })
      setItems((prev) => {
        const seen = new Set(prev.map((n) => n.id))
        return [...prev, ...data.items.filter((n) => !seen.has(n.id))]
      })
      setTotal(data.total)
      setUnreadCount(data.unreadCount)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    } finally {
      setLoadingMore(false)
    }
  }

  const sections = useMemo(() => notificationSections(items), [items])

  async function handleMarkRead(n: AppNotification) {
    if (!isUnread(n)) return
    try {
      await markRead(n.id)
      setItems((prev) =>
        prev.map((row) =>
          row.id === n.id ? { ...row, readAt: new Date().toISOString() } : row
        )
      )
      setUnreadCount((c) => Math.max(0, c - 1))
      await refreshBadge()
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  async function handleMarkAll() {
    setBusy(true)
    try {
      await markAllRead()
      setItems((prev) =>
        prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))
      )
      setUnreadCount(0)
      showToast('Alle notifikationer markeret som læst.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    } finally {
      setBusy(false)
    }
  }

  async function handleRemove(n: AppNotification) {
    try {
      await deleteNotification(n.id)
      const wasUnread = isUnread(n)
      setItems((prev) => prev.filter((row) => row.id !== n.id))
      setTotal((t) => Math.max(0, t - 1))
      if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1))
      await refreshBadge()
      showToast('Notifikation fjernet.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  const summary = inboxSummary(total, unreadCount, filtersActive(filters))

  const content = (
    <>
      {!embedded && (
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-[26px] leading-[1.15] text-ink">Notifikationer</h1>
            {!loading && <p className="text-sm text-ink-500 mt-1">{summary}</p>}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleMarkAll()}
              className="shrink-0 inline-flex h-[38px] items-center rounded-[7px] border border-rule-strong bg-paper px-3.5 text-sm font-semibold text-ink hover:border-ink-300 disabled:opacity-50 transition-colors"
            >
              Markér alle læst
            </button>
          )}
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between gap-3 mb-3">
          {!loading && (
            <p className="text-sm text-ink-500">{summary}</p>
          )}
          {unreadCount > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleMarkAll()}
              className="shrink-0 inline-flex h-[38px] items-center rounded-[7px] border border-rule-strong bg-paper px-3.5 text-sm font-semibold text-ink hover:border-ink-300 disabled:opacity-50 transition-colors"
            >
              Markér alle læst
            </button>
          )}
        </div>
      )}

      {(facets || filtersActive(filters)) && (
        <InboxFilters
          value={filters}
          facets={facets}
          unreadCount={unreadCount}
          onChange={setFilters}
        />
      )}

      {loading && <p className="text-sm text-ink-500">Henter notifikationer…</p>}

      {!loading && items.length === 0 && !filtersActive(filters) && (
        <p className="text-sm text-ink-500 bg-paper border border-rule rounded-[10px] px-4 py-8 text-center">
          Du får besked her, når noget på din watchlist ændrer sig i CVR.
        </p>
      )}

      {!loading && items.length === 0 && filtersActive(filters) && (
        <div className="text-sm text-ink-500 bg-paper border border-rule rounded-[10px] px-4 py-8 text-center">
          Ingen notifikationer matcher filtrene.{' '}
          <button
            type="button"
            onClick={() => setFilters({ unreadOnly: false, groups: [], listId: null })}
            className="font-semibold text-ink underline decoration-ink-300 underline-offset-[3px] hover:decoration-ink"
          >
            Nulstil filtre
          </button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <ul className="overflow-hidden rounded-[10px] border border-rule bg-paper divide-y divide-rule-soft">
          {sections.map((section) => (
            <React.Fragment key={section.id}>
              <li className="px-5 py-2 bg-canvas/70">
                <h2 className="text-[13px] font-semibold text-ink-500">
                  {section.label}
                </h2>
              </li>
              {section.items.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  watchlistItem={
                    n.entityType && n.entityId
                      ? findWatchlistItem(
                          watchlistData,
                          n.entityType as WatchlistEntityType,
                          n.entityId
                        )
                      : undefined
                  }
                  onMarkRead={() => void handleMarkRead(n)}
                  onRemove={() => void handleRemove(n)}
                  onOpenSettings={(item) => setSettingsItem(item)}
                />
              ))}
            </React.Fragment>
          ))}
        </ul>
      )}

      {!loading && items.length < total && (
        <div className="mt-4 text-center">
          <button
            type="button"
            disabled={loadingMore}
            onClick={() => void handleLoadMore()}
            className="text-[13px] font-semibold text-ink-700 underline decoration-ink-300 underline-offset-[3px] hover:text-ink hover:decoration-ink disabled:opacity-50"
          >
            {loadingMore ? 'Henter…' : `Vis flere (${total - items.length})`}
          </button>
        </div>
      )}

      {settingsItem?.notificationPreferences && (
        <WatchlistItemNotificationModal
          open
          onClose={() => setSettingsItem(null)}
          displayName={settingsItem.displayName || settingsItem.entityId}
          entityType={settingsItem.entityType}
          preferences={settingsItem.notificationPreferences}
          showGlobalLink
          onSave={async (updates) => {
            try {
              const updated = await updateItemNotificationPreferences(
                settingsItem.id,
                updates
              )
              setSettingsItem(updated)
              showToast('Notifikationer opdateret.')
              return updated.notificationPreferences ?? { types: [] }
            } catch (err: unknown) {
              showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
              throw err
            }
          }}
        />
      )}
    </>
  )

  if (embedded) return content

  return (
    <Container width="prose" className="py-8">
      {content}
    </Container>
  )
}

function NotificationRow({
  notification: n,
  watchlistItem,
  onMarkRead,
  onRemove,
  onOpenSettings,
}: {
  notification: AppNotification
  watchlistItem?: WatchlistItem
  onMarkRead: () => void
  onRemove: () => void
  onOpenSettings: (item: WatchlistItem) => void
}) {
  const navigate = useNavigate()
  const href = notificationHref(n)
  const unread = isUnread(n)
  const entityLabel = useMemo(
    () => notificationEntityLabel(n, watchlistItem),
    [n, watchlistItem]
  )
  const canOpenEntitySettings = Boolean(
    watchlistItem?.notificationPreferences && n.entityType && n.entityId
  )

  const title = href ? (
    <Link
      to={href}
      onClick={onMarkRead}
      className={`${unread ? 'font-semibold text-ink' : 'font-normal text-ink-600'} text-base hover:underline decoration-ink-300 underline-offset-[3px] block`}
    >
      {n.title}
    </Link>
  ) : (
    <p className={unread ? 'text-base font-semibold text-ink' : 'text-base font-normal text-ink-600'}>
      {n.title}
    </p>
  )

  return (
    <li className="group px-5 py-3.5 hover:bg-[#FAFBFC] transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <NotificationItemContent notification={n} title={title} />
        </div>

        <div
          className={`flex shrink-0 items-center gap-0.5 transition-opacity ${
            unread
              ? 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100'
              : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
          }`}
        >
          {unread && (
            <button
              type="button"
              onClick={onMarkRead}
              aria-label="Markér som læst"
              title="Markér som læst"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:bg-rule-soft hover:text-ink transition-colors"
            >
              <Check className="h-4 w-4" aria-hidden />
            </button>
          )}

          <Menu
            dense
            trigger={<MoreHorizontal className="h-4 w-4" aria-hidden />}
            triggerAriaLabel="Notifikationshandlinger"
            align="right"
            triggerClassName="p-1.5"
          >
            {(close) => (
              <>
                <MenuItem
                  danger
                  icon={<Trash2 className="h-3.5 w-3.5" />}
                  onClick={() => {
                    close()
                    onRemove()
                  }}
                >
                  Fjern fra indbakke
                </MenuItem>
                <MenuSeparator />
                <MenuItem
                  icon={<Bell className="h-3.5 w-3.5" />}
                  disabled={!canOpenEntitySettings}
                  onClick={() => {
                    if (!watchlistItem) return
                    close()
                    onOpenSettings(watchlistItem)
                  }}
                >
                  Indstillinger for {entityLabel}
                </MenuItem>
                <MenuItem
                  icon={<Settings className="h-3.5 w-3.5" />}
                  onClick={() => {
                    close()
                    navigate('/account/notifikationer/indstillinger')
                  }}
                >
                  Globale indstillinger
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </div>
    </li>
  )
}

export default NotificationsInbox
